import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ClientType } from '../models/Enums';
import { SimulatorsService } from 'src/app/pages/content/simulators/simulators.service';
import { LauncherUpdateModalComponent } from 'src/app/components/launcher-update-modal/launcher-update-modal.component';
import { RealtimeService } from './realtime.service';
import * as semver from 'semver';

export interface OutdatedBay {
  simulatorIdentifier: string;
  location: string;
  bay: string;
  version: string;
}

interface LegacyConnection {
  simId?: string;
  type?: ClientType | string;
}

const POLL_MS = 30000;
const PASSED_KEY = 'bm-launcher-check-passed';

@Injectable({
  providedIn: 'root',
})
export class LauncherUpdateGuardService {
  readonly outdatedBays$ = new BehaviorSubject<OutdatedBay[]>([]);
  readonly checking$ = new BehaviorSubject<boolean>(false);
  readonly blocked$ = new BehaviorSubject<boolean>(false);
  private timer: ReturnType<typeof setInterval> | null = null;
  private dialogRef: MatDialogRef<LauncherUpdateModalComponent> | null = null;
  private bays = new Map<string, OutdatedBay>();
  private emptyPolls = 0;
  private companyId: number | null = null;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private simulatorsService: SimulatorsService,
    private realtimeService: RealtimeService,
  ) {}

  beginCheck(companyId: number): void {
    if (!environment.legacyConnectionsUrl || this.hasPassed(companyId)) {
      return;
    }
    this.checking$.next(true);
    this.open();
  }

  async start(companyId: number): Promise<void> {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (!environment.legacyConnectionsUrl || this.hasPassed(companyId)) {
      return;
    }
    this.beginCheck(companyId);
    this.companyId = companyId;
    this.emptyPolls = 0;
    await this.check(true);
    this.checking$.next(false);
    if (!this.hasPassed(companyId)) {
      this.timer = setInterval(() => void this.check(), POLL_MS);
    }
  }

  private hasPassed(companyId: number): boolean {
    try {
      return sessionStorage.getItem(PASSED_KEY) === String(companyId);
    } catch {
      return false;
    }
  }

  private markPassed(): void {
    try {
      sessionStorage.setItem(PASSED_KEY, String(this.companyId));
    } catch {}
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async loadBays(): Promise<boolean> {
    if (this.companyId == null) {
      return false;
    }
    try {
      const locations = await lastValueFrom(
        this.simulatorsService.getLocationsAndSimulatorsSelection(
          this.companyId,
        ),
      );
      this.bays.clear();
      for (const location of locations || []) {
        for (const sim of location.simulators || []) {
          this.bays.set(sim.simulatorIdentifier, {
            simulatorIdentifier: sim.simulatorIdentifier,
            location: location.name,
            bay: sim.name,
            version: sim.launcherVersion || '',
          });
        }
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.emptyPolls = 0;
    this.companyId = null;
    try {
      sessionStorage.removeItem(PASSED_KEY);
    } catch {}
    this.checking$.next(false);
    this.outdatedBays$.next([]);
    this.close();
  }

  private async check(manual = false): Promise<void> {
    const [connections, , loaded] = await Promise.all([
      lastValueFrom(
        this.http.get<LegacyConnection[]>(environment.legacyConnectionsUrl),
      ).catch((): LegacyConnection[] => []),
      this.realtimeService.refreshOnlineSimulators().catch(() => []),
      this.loadBays(),
    ]);
    if (!loaded) {
      if (this.checking$.value) {
        this.close();
      }
      return;
    }
    const legacyIds = new Set(
      (connections || [])
        .filter(
          (c) => Number(c.type) === ClientType.SimulatorTerminal && c.simId,
        )
        .map((c) => c.simId as string),
    );
    const outdated = new Map<string, OutdatedBay>();
    for (const bay of this.bays.values()) {
      if (this.realtimeService.isSimulatorOnline(bay.simulatorIdentifier)) {
        continue;
      }
      const version = semver.coerce(bay.version);
      const reportedOld =
        !!version && semver.lt(version, environment.sseLauncherVersion);
      if (legacyIds.has(bay.simulatorIdentifier) || reportedOld) {
        outdated.set(bay.simulatorIdentifier, bay);
      }
    }
    if (outdated.size) {
      this.emptyPolls = 0;
      this.outdatedBays$.next(
        Array.from(outdated.values()).sort(
          (a, b) =>
            a.location.localeCompare(b.location) || a.bay.localeCompare(b.bay),
        ),
      );
      this.open();
    } else if (manual || ++this.emptyPolls >= 2) {
      this.outdatedBays$.next([]);
      this.close();
      this.markPassed();
    }
  }

  private open(): void {
    this.blocked$.next(true);
    if (this.dialogRef) {
      return;
    }
    this.dialogRef = this.dialog.open(LauncherUpdateModalComponent, {
      disableClose: true,
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'launcher-update-panel',
      data: {
        bays$: this.outdatedBays$,
        checking$: this.checking$,
        refresh: () => this.check(true),
      },
    });
    this.dialogRef.afterClosed().subscribe(() => (this.dialogRef = null));
  }

  private close(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
    this.blocked$.next(false);
  }
}
